import { httpClient as http } from '@/utils/http'

// API基础路径
const API_BASE = '/mdm/country'

// 基础分页请求
export interface PageRequest {
  pageNum: number
  pageSize: number
}

// 分页结果
export interface PageResultVO<T> {
  records: T[]
  total: number
  pageNum: number
  pageSize: number
}

// 批量删除请求
export interface BatchDeleteRequest {
  ids: string[]
}

// 国家分页查询请求
export interface CountryPageRequest extends PageRequest {
  tenantId?: string
  countryNameEn?: string
  countryNameCn?: string
  countryCode?: string
  continentId?: string
  areaCode?: string
  enabled?: number
}

// 国家列表项视图对象
export interface CountryListVO {
  id: string
  tenantId?: string
  countryNameEn: string
  countryNameCn: string
  countryCode: string
  continentId?: string
  areaCode?: string
  enabled: number
  isPlatformData?: boolean
  dateCreated?: string
  dateModified?: string
}

// 国家详情视图对象
export interface CountryVO {
  id: string
  tenantId?: string
  countryNameEn: string
  countryNameCn: string
  countryCode: string
  continentId?: string
  areaCode?: string
  enabled: number
  isPlatformData?: boolean
  ediCodes?: CommonEdiCodeVO[]
}

// 国家保存请求
export interface CountrySaveRequest {
  tenantId?: string
  countryNameEn: string
  countryNameCn: string
  countryCode: string
  continentId?: string
  areaCode?: string
  enabled?: number
}

// 国家更新请求
export interface CountryUpdateRequest extends CountrySaveRequest {
  id: string
}

// 模糊搜索请求
export interface FuzzySearchRequest {
  tenantId?: string
  keyword: string
  pageNum?: number
  pageSize?: number
}

// 国家EDI查询请求
export interface CountryEdiQueryRequest {
  countryCode: string
}

// 国家EDI保存请求
export interface CountryEdiSaveRequest {
  countryCode: string
  platform: string
  ediCode: string
}

// 国家EDI代码信息
export interface CountryEdiCodeInfo {
  platform: string
  ediCode: string
}

// 批量国家EDI保存请求
export interface BatchCountryEdiSaveRequest {
  countryCode: string
  ediCodes: CountryEdiCodeInfo[]
}

// 国家EDI检查请求
export interface CountryEdiCheckRequest {
  platform: string
  ediCode: string
}

// 通用EDI代码视图对象
export interface CommonEdiCodeVO {
  id: string
  tenantId?: string
  platform: string
  ediCode: string
  ediType: number
  businessEntityCode?: string
  enabled: number
  dateCreated: string
  dateModified: string
}

// 国家API
export const countryApi = {
  // 分页查询国家列表
  page: (params: CountryPageRequest) => {
    return http.post<PageResultVO<CountryListVO>>(`${API_BASE}/queryPage`, params)
  },

  // 查询国家列表
  list: (params: CountryPageRequest) => {
    return http.post<CountryListVO[]>(`${API_BASE}/queryList`, params)
  },

  // 模糊搜索国家
  search: (params: FuzzySearchRequest) => {
    return http.post<CountryListVO[]>(`${API_BASE}/search`, params)
  },

  // 根据ID获取国家详情
  get: (id: string) => {
    return http.post<CountryVO>(`${API_BASE}/getById`, { id })
  },

  // 保存国家（新增或更新）
  save: (params: CountrySaveRequest) => {
    return http.post<boolean>(`${API_BASE}/save`, params)
  },

  // 更新国家
  update: (params: CountryUpdateRequest) => {
    return http.post<boolean>(`${API_BASE}/save`, params)
  },

  // 删除国家
  delete: (id: string) => {
    return http.post<boolean>(`${API_BASE}/delete`, { id })
  },

  // 批量保存国家
  batchSave: (requests: CountrySaveRequest[]) => {
    return http.post<boolean>(`${API_BASE}/batchSave`, requests)
  },

  // 批量删除国家
  batchDelete: (params: BatchDeleteRequest) => {
    return http.post<boolean>(`${API_BASE}/batchDelete`, {
      ids: params.ids
    })
  },

  // 更新状态
  updateStatus: (ids: string[], enabled: number) => {
    return http.post<boolean>(`${API_BASE}/updateStatus`, {
      ids,
      enabled
    })
  },

  // 批量更新状态
  batchUpdateStatus: (ids: string[], enabled: number) => {
    return http.post<boolean>(`${API_BASE}/batchUpdateStatus`, {
      ids,
      enabled
    })
  },

  // 单个更新状态
  updateSingleStatus: (id: string, enabled: number) => {
    return http.post<boolean>(`${API_BASE}/updateSingleStatus`, { id, enabled })
  },

  // 根据国家代码查询国家
  queryByCode: (params: { countryCode: string }) => {
    return http.post<CountryVO>(`${API_BASE}/queryByCode`, params)
  },

  // 根据大洲ID查询国家列表
  queryByContinent: (params: { continentId: string }) => {
    return http.post<CountryListVO[]>(`${API_BASE}/queryByContinent`, params)
  },

  // 查询启用状态的国家列表
  queryEnabled: (params: { enabled?: number }) => {
    return http.post<CountryListVO[]>(`${API_BASE}/queryEnabled`, params)
  },

  // 根据国家名称查询
  queryByName: (params: { nameCn?: string; nameEn?: string; enabled?: number }) => {
    return http.post<CountryListVO[]>(`${API_BASE}/queryByName`, params)
  },

  // 根据区域查询国家
  queryByRegion: (params: { region: string; enabled?: number }) => {
    return http.post<CountryListVO[]>(`${API_BASE}/queryByRegion`, params)
  },

  // 查询国家的EDI代码列表
  queryEdiCodes: (params: CountryEdiQueryRequest) => {
    return http.post<CommonEdiCodeVO[]>(`${API_BASE}/edi/queryList`, params)
  },

  // 为国家添加EDI代码
  saveEdiCode: (params: CountryEdiSaveRequest) => {
    return http.post<boolean>(`${API_BASE}/edi/save`, params)
  },

  // 批量为国家添加EDI代码
  batchSaveEdiCodes: (params: BatchCountryEdiSaveRequest) => {
    return http.post<boolean>(`${API_BASE}/edi/batchSave`, params)
  },

  // 删除国家的EDI代码
  deleteEdiCode: (id: string) => {
    return http.post<boolean>(`${API_BASE}/edi/delete`, { id })
  },

  // 批量删除国家的EDI代码
  batchDeleteEdiCodes: (params: BatchDeleteRequest) => {
    return http.post<boolean>(`${API_BASE}/edi/batchDelete`, {
      ids: params.ids
    })
  },

  // 检查国家EDI代码是否存在
  checkEdiCodeExists: (params: CountryEdiCheckRequest) => {
    return http.post<boolean>(`${API_BASE}/edi/checkExists`, params)
  },

  // 查询可关联的平台国家（分页）
  queryPlatformPage: (params: CountryPageRequest) => {
    return http.post<PageResultVO<CountryListVO>>(`${API_BASE}/queryPlatformPage`, params)
  },

  // 查询可关联的平台国家（列表）
  queryPlatformList: (params: CountryPageRequest) => {
    return http.post<CountryListVO[]>(`${API_BASE}/queryPlatformList`, params)
  },

  // 批量关联平台国家
  batchAssociate: (platformIds: string[]) => {
    return http.post<boolean>(`${API_BASE}/batchAssociate`, {
      platformIds
    })
  },

  // 批量解除关联平台国家
  batchDisassociate: (associationIds?: string[], platformIds?: string[]) => {
    return http.post<boolean>(`${API_BASE}/batchDisassociate`, {
      associationIds,
      platformIds
    })
  }
}
